import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { bootstrapTracking, trackPageView, trackScroll } from "./lib/tracking";
import { captureUTM } from "./lib/utm";
import { SettingsProvider } from "./lib/settings";
import SEO from "./components/SEO";

import Landing from "./pages/Landing";
import Checkout from "./pages/Checkout";
import Success from "./pages/Success";
import Cancel from "./pages/Cancel";
import PaymentReturn from "./pages/PaymentReturn";
import ContractReturn from "./pages/ContractReturn";
import AdminLogin from "./pages/AdminLogin";
import AdminMagic from "./pages/AdminMagic";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsConditions from "./pages/TermsConditions";
import BlackFridayRules from "./pages/BlackFridayRules";
import NotFound from "./pages/NotFound";
import Webinars from "./pages/Webinars";
import WebinarDetail from "./pages/WebinarDetail";
import CerereAdeverinta from "./pages/CerereAdeverinta";
import DateCurs from "./pages/DateCurs";
import EvaluareInitiala from "./pages/EvaluareInitiala";
import Feedback from "./pages/Feedback";
import VerificaAdeverinta from "./pages/VerificaAdeverinta";
import Portal from "./pages/Portal";
import PortalEnter from "./pages/PortalEnter";
import PortalActivity from "./pages/PortalActivity";
import PortalGroup from "./pages/PortalGroup";
import PortalParticipant from "./pages/PortalParticipant";
import PortalPresence from "./pages/PortalPresence";
import PortalReports from "./pages/PortalReports";
import PortalGuide from "./pages/PortalGuide";
import PortalReport from "./pages/PortalReport";
import PortalPortfolio from "./pages/PortalPortfolio";
import PortalPortfolios from "./pages/PortalPortfolios";
import PortalEvaluation from "./pages/PortalEvaluation";

import { scrollToHash } from "./lib/scrollToHash";

import "./App.css";

function ScrollTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
    captureUTM();
    // `/#grupe` must land on the section. Scrolling to the top unconditionally
    // is what made every anchor link open the top of the homepage instead.
    scrollToHash(location.hash);
  }, [location]);

  useEffect(() => {
    let fired50 = false;
    let fired90 = false;
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.body.scrollHeight;
      const pct = total > 0 ? (scrolled / total) * 100 : 0;
      if (!fired50 && pct >= 50) { fired50 = true; trackScroll(50); }
      if (!fired90 && pct >= 90) { fired90 = true; trackScroll(90); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return null;
}

/** `/inscriere/<code>` — a cohort reached only through its own link (07.10.2026). */
function CheckoutByLink() {
  const { code } = useParams();
  return <Checkout linkCode={code} />;
}

function NoIndex({ children, title }) {
  // M917 — Wraps transactional/admin routes that must NEVER be indexed by
  // search engines. Belt-and-suspenders: robots.txt already disallows these
  // paths, but `noindex` on the rendered HTML guards against accidental
  // links and prevents Googlebot from showing stale snippets if a path is
  // ever crawled before robots.txt is fetched.
  return (
    <>
      <SEO noindex title={title || "FPED"} />
      {children}
    </>
  );
}

function App() {
  useEffect(() => {
    bootstrapTracking();
    captureUTM();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <ScrollTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/checkout" element={<NoIndex title="Finalizare comandă | FPED"><Checkout /></NoIndex>} />
            <Route path="/inscriere/:code" element={<NoIndex title="Înscriere | FPED"><CheckoutByLink /></NoIndex>} />
            <Route path="/success" element={<NoIndex title="Comandă confirmată | FPED"><Success /></NoIndex>} />
            <Route path="/cancel" element={<NoIndex title="Comandă anulată | FPED"><Cancel /></NoIndex>} />
            <Route path="/payment/success" element={<NoIndex title="Plată confirmată | FPED"><PaymentReturn variant="success" /></NoIndex>} />
            <Route path="/payment/cancel" element={<NoIndex title="Plată anulată | FPED"><PaymentReturn variant="cancel" /></NoIndex>} />
            <Route path="/contract/semnat" element={<NoIndex title="Contract semnat | FPED"><ContractReturn variant="signed" /></NoIndex>} />
            <Route path="/contract/refuzat" element={<NoIndex title="Contract refuzat | FPED"><ContractReturn variant="declined" /></NoIndex>} />
            <Route path="/payment/failed" element={<NoIndex title="Plată eșuată | FPED"><PaymentReturn variant="failed" /></NoIndex>} />
            <Route path="/politica-confidentialitate" element={<PrivacyPolicy />} />
            <Route path="/webinarii" element={<Webinars />} />
            <Route path="/webinarii/:slug" element={<WebinarDetail />} />
            <Route path="/adeverinta/:slug" element={<CerereAdeverinta />} />
            <Route path="/evaluare-initiala" element={<NoIndex title="Evaluarea inițială | FPED"><EvaluareInitiala /></NoIndex>} />
            {/* The final feedback questionnaire (30.10.2026): the personal link, and the trainer's group link. */}
            <Route path="/feedback" element={<NoIndex title="Chestionar de feedback | FPED"><Feedback /></NoIndex>} />
            <Route path="/feedback/:code" element={<NoIndex title="Chestionar de feedback | FPED"><Feedback /></NoIndex>} />
            <Route path="/date-curs/:token" element={<NoIndex title="Date pentru dosarul de acreditare | FPED"><DateCurs /></NoIndex>} />
            <Route path="/verifica-adeverinta" element={<VerificaAdeverinta />} />
            {/* The participants' and trainers' portal. Not linked from the site;
                reached from the emails, and only for a group whose portal is on. */}
            <Route path="/portal" element={<NoIndex title="Portalul cursului | FPED"><Portal /></NoIndex>} />
            <Route path="/portal/ghid" element={<NoIndex title="Ghidul portalului | FPED"><PortalGuide /></NoIndex>} />
            <Route path="/portal/raportari" element={<NoIndex title="Raportările mele | Portalul cursului | FPED"><PortalReports /></NoIndex>} />
            <Route path="/portal/raportari/:ticketId" element={<NoIndex title="Raportare | Portalul cursului | FPED"><PortalReport /></NoIndex>} />
            <Route path="/portal/intrare" element={<NoIndex title="Intrare în portal | FPED"><PortalEnter /></NoIndex>} />
            <Route path="/portal/tema/:activityId" element={<NoIndex title="Temă | Portalul cursului | FPED"><PortalActivity /></NoIndex>} />
            <Route path="/portal/grupa/:groupId" element={<NoIndex title="Grupa | Portalul cursului | FPED"><PortalGroup /></NoIndex>} />
            <Route path="/portal/portofoliu/:groupId" element={<NoIndex title="Portofoliul final | Portalul cursului | FPED"><PortalPortfolio /></NoIndex>} />
            <Route path="/portal/grupa/:groupId/portofolii" element={<NoIndex title="Portofoliile | Portalul cursului | FPED"><PortalPortfolios /></NoIndex>} />
            <Route path="/portal/evaluare/:groupId" element={<NoIndex title="Evaluarea portofoliilor | Portalul cursului | FPED"><PortalEvaluation /></NoIndex>} />
            <Route path="/portal/grupa/:groupId/prezenta" element={<NoIndex title="Prezența | Portalul cursului | FPED"><PortalPresence /></NoIndex>} />
            <Route path="/portal/grupa/:groupId/cursant/:enrollmentId" element={<NoIndex title="Cursant | Portalul cursului | FPED"><PortalParticipant /></NoIndex>} />
            <Route path="/politica-cookies" element={<CookiePolicy />} />
            <Route path="/termeni-conditii" element={<TermsConditions />} />
            <Route path="/regulament-black-friday" element={<BlackFridayRules />} />
            <Route path="/admin/login" element={<NoIndex title="Admin · Login | FPED"><AdminLogin /></NoIndex>} />
            <Route path="/admin/magic" element={<NoIndex title="Admin · Autentificare | FPED"><AdminMagic /></NoIndex>} />
            {/* M904 — Admin dashboard URL routing.
                The sub-section (leads/enrollments/groups/webinars/…) is captured
                as a route param so refresh, browser back/forward and deep
                linking all work. Empty `/admin` redirects to the historic
                default tab (`leads`); the dashboard internally maps unknown
                slugs back to it as well, so users can't land on a blank tab. */}
            <Route path="/admin" element={<Navigate to="/admin/leads" replace />} />
            <Route path="/admin/:tab" element={<NoIndex title="Admin · FPED"><AdminDashboard /></NoIndex>} />
            <Route path="*" element={<NoIndex title="Pagina nu a fost găsită | FPED"><NotFound /></NoIndex>} />
          </Routes>
          <Toaster richColors position="top-center" />
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
